/**
 * Typed client for /board/* — backs the Board Governance screens.
 *
 * Board governance is a STAFF / board-INTERNAL surface (not customer-
 * facing). It manages the full meeting lifecycle:
 *
 *   scheduled → in_progress → ended → concluded
 *                                  (cancelled is a terminal branch off
 *                                   scheduled)
 *
 * Three audiences exist server-side (admin / board member / shareholder)
 * but admin.nurev.io always authenticates as admin, so every endpoint
 * here is exercised with full access.
 *
 * ENVELOPE: every /board/* endpoint returns the standard
 * `{ success: true, data: {...} }` envelope via the API's
 * `utils.handleSuccess(res, code, {...})`. Verified against
 * nurev-api/app/controllers/board.js + app/middleware/utils.js
 * (handleSuccess wraps the third arg under `data`). The admin `client`
 * does NOT auto-unwrap, so every call uses `unwrap()` to strip the one
 * envelope level. (The legacy dashboard hook returned raw axios calls
 * whose axiosClient stripped one `.data`, then the legacy screens used
 * defensive `data?.data?.x || data?.x` reads — that variance collapses
 * to a single deterministic `unwrap()` here.)
 */
import { client, unwrap } from './client';

// ─── Enums / unions ─────────────────────────────────────────────────────────

export type MeetingStatus =
  | 'scheduled'
  | 'in_progress'
  | 'ended'
  | 'concluded'
  | 'cancelled';

export type MeetingType = 'quarterly' | 'emergency' | 'annual';

export type VotingItemType = 'binary' | 'information' | 'multi';

export type VotingOutcome =
  | 'pending'
  | 'passed'
  | 'failed'
  | 'no_quorum'
  | 'tabled'
  | null;

export type VoteChoice = 'yes' | 'no' | 'abstain' | string;

export type AttendanceStatus = 'present' | 'unavailable' | 'pending' | null;

// ─── Entities ───────────────────────────────────────────────────────────────

export interface BoardMember {
  _id: string;
  name: string;
  email: string;
  classAShares: number;
  boardRole?: 'chair' | 'member' | string;
  boardJoinedAt?: string | null;
}

export interface BoardMeeting {
  _id: string;
  name: string;
  description?: string;
  meetingType: MeetingType;
  scheduledAt: string;
  status: MeetingStatus;
  startedAt?: string | null;
  endedAt?: string | null;
  concludedAt?: string | null;
  cancelledAt?: string | null;
  recordingUrl?: string | null;
  recordingSubmittedAt?: string | null;
  minutes?: string;
  minutesUpdatedAt?: string | null;
  quorumRequired?: number;
  executiveSession?: boolean;
  resultsSummary?: unknown;
}

export interface VotingItemChoice {
  key: string;
  label: string;
}

export interface VotingItem {
  _id: string;
  meetingId: string;
  order: number;
  title: string;
  description?: string;
  itemType: VotingItemType;
  choices?: VotingItemChoice[];
  threshold?: string;
  confidential?: boolean;
  conflictedShareholderIds?: string[];
  outcome?: VotingOutcome;
  tally?: unknown;
}

/**
 * Attendance rows come back with `shareholderId` populated to the
 * Shareholder doc (name / email / classAShares / boardRole) — so it's an
 * object, not just an id string. The legacy screen guarded for the
 * un-populated case (`typeof sh !== 'object'`); we keep that guard.
 */
export interface PopulatedShareholderRef {
  _id: string;
  name: string;
  email?: string;
  classAShares: number;
  boardRole?: string;
}

export interface MeetingAttendance {
  _id: string;
  meetingId: string;
  shareholderId: PopulatedShareholderRef | string;
  status: AttendanceStatus;
  markedAt?: string | null;
  sharesAtMarking?: number;
}

export interface Vote {
  _id: string;
  meetingId: string;
  votingItemId: string;
  shareholderId: PopulatedShareholderRef | string;
  choice: VoteChoice;
  votedSharesAt: number;
  comment?: string;
  castAt?: string;
}

export interface ResultsEmailDraft {
  subject?: string | null;
  html?: string | null;
  text?: string | null;
  editedAt?: string | null;
  editedBy?: string | null;
}

// ─── Response shapes ──────────────────────────────────────────────────────────

export interface MeetingListResponse {
  meetings: BoardMeeting[];
}

export interface BoardMembersResponse {
  members: BoardMember[];
}

export interface MeetingDetailResponse {
  meeting: BoardMeeting;
  items: VotingItem[];
  attendance: MeetingAttendance[];
  actionItems: unknown[];
  votes: Vote[];
}

export interface ResultsDraftResponse {
  draft: ResultsEmailDraft | null;
  resultsSummary?: unknown;
  meetingName?: string;
}

export interface MeetingMutationResponse {
  meeting: BoardMeeting;
}

export interface PublishResultsResponse {
  meeting: BoardMeeting;
  emailsSent: number;
}

// ─── Request bodies ───────────────────────────────────────────────────────────

export interface CreateMeetingInput {
  name: string;
  meetingType: MeetingType;
  description?: string;
  scheduledAt: string;
  executiveSession?: boolean;
}

export interface AddVotingItemInput {
  title: string;
  description?: string;
  itemType: VotingItemType;
}

export interface CastVoteInput {
  choice: VoteChoice;
  comment?: string;
}

export interface UpdateResultsDraftInput {
  subject?: string;
  html?: string;
  text?: string;
}

// ─── Reads ─────────────────────────────────────────────────────────────────────

export async function fetchMeetings(): Promise<MeetingListResponse> {
  return unwrap<MeetingListResponse>(
    client.get('/board/meetings')
  );
}

export async function fetchMeeting(id: string): Promise<MeetingDetailResponse> {
  return unwrap<MeetingDetailResponse>(
    client.get(`/board/meetings/${id}`)
  );
}

export async function fetchBoardMembers(): Promise<BoardMembersResponse> {
  return unwrap<BoardMembersResponse>(
    client.get('/board/members')
  );
}

export async function fetchResultsDraft(
  id: string
): Promise<ResultsDraftResponse> {
  return unwrap<ResultsDraftResponse>(
    client.get(`/board/meetings/${id}/results-draft`)
  );
}

// ─── Mutations — meeting lifecycle ──────────────────────────────────────────────

export async function createMeeting(
  payload: CreateMeetingInput
): Promise<MeetingMutationResponse> {
  return unwrap<MeetingMutationResponse>(
    client.post('/board/meetings', payload)
  );
}

export async function cancelMeeting(
  id: string
): Promise<MeetingMutationResponse> {
  return unwrap<MeetingMutationResponse>(
    client.delete(`/board/meetings/${id}`)
  );
}

export async function startMeeting(
  id: string
): Promise<MeetingMutationResponse> {
  return unwrap<MeetingMutationResponse>(
    client.post(`/board/meetings/${id}/start`)
  );
}

export async function endMeeting(id: string): Promise<unknown> {
  // Returns { meeting, itemResults, quorum } — only `meeting` is typed
  // here since the detail screen just refetches after end.
  return unwrap<unknown>(client.post(`/board/meetings/${id}/end`));
}

export async function publishResults(
  id: string
): Promise<PublishResultsResponse> {
  return unwrap<PublishResultsResponse>(
    client.post(`/board/meetings/${id}/publish`)
  );
}

export async function updateResultsDraft(
  id: string,
  payload: UpdateResultsDraftInput
): Promise<{ draft: ResultsEmailDraft }> {
  return unwrap<{ draft: ResultsEmailDraft }>(
    client.patch(`/board/meetings/${id}/results-draft`, payload)
  );
}

export async function updateMinutes(
  id: string,
  minutes: string
): Promise<MeetingMutationResponse> {
  return unwrap<MeetingMutationResponse>(
    client.patch(`/board/meetings/${id}/minutes`, { minutes })
  );
}

export async function submitRecording(
  id: string,
  recordingUrl: string
): Promise<MeetingMutationResponse> {
  return unwrap<MeetingMutationResponse>(
    client.patch(`/board/meetings/${id}/recording`, { recordingUrl })
  );
}

export async function addVotingItem(
  id: string,
  payload: AddVotingItemInput
): Promise<{ item: VotingItem }> {
  return unwrap<{ item: VotingItem }>(
    client.post(`/board/meetings/${id}/items`, payload)
  );
}

// ─── Mutations — board member self-service ──────────────────────────────────────

export async function markAttendance(
  id: string,
  status: AttendanceStatus
): Promise<{ attendance: MeetingAttendance }> {
  return unwrap<{ attendance: MeetingAttendance }>(
    client.post(`/board/meetings/${id}/attendance`, { status })
  );
}

export async function castVote(
  meetingId: string,
  itemId: string,
  payload: CastVoteInput
): Promise<unknown> {
  // Returns { vote, updated } — detail screen just refetches after cast.
  return unwrap<unknown>(
    client.post(
      `/board/meetings/${meetingId}/items/${itemId}/vote`,
      payload
    )
  );
}

export async function declareConflict(
  meetingId: string,
  itemId: string
): Promise<{ item: VotingItem }> {
  return unwrap<{ item: VotingItem }>(
    client.post(`/board/meetings/${meetingId}/items/${itemId}/conflict`)
  );
}
